import React from 'react';
import { bffQuery } from '@parsina/framework/clinical';
import { isIranMarket } from '@parsina/framework/ui/lib/brand';

import { countryForPhone } from '../../constants/countries';
import { CURRENCY_CODES, DEFAULT_CURRENCY } from '../../lib/currency';

/**
 * Where the account's number is from — so a founder who signed in from abroad
 * registers a clinic without a word of Iran in the wizard (PM-0061).
 *
 * The answer is the doctor BFF's `onboardingContext`, the same one the doctor
 * track already asks (DoctorTrack.jsx): ONE rule for "foreign", decided
 * server-side from the number the account signed in with. Anything short of a
 * positive answer — an older BFF, a network error, a number in national form —
 * is "not foreign", which is exactly how the wizard behaved before.
 *
 * A BRAND outside the Iranian market (`market: 'intl'`, docs/rebranding/
 * CONTRACT.md) has no Iranian side at all: EVERY account onboards as from abroad
 * — no کد نظام پزشکی, no IRR, no +98, no Iranian provinces — whatever its number,
 * and a +98 number does not make the country Iran. A failed lookup is then
 * "abroad, country unknown", never "Iran".
 */
const ONBOARDING_CONTEXT = `
  query OnboardingContext {
    onboardingContext { foreign phone }
  }
`;

const HOME = Object.freeze({ foreign: false, country: null });
const ABROAD = Object.freeze({ foreign: true, country: null });

/** What "nobody knows yet" (or "the BFF could not say") means for this market. */
const unknownRegion = () => (isIranMarket() ? HOME : ABROAD);

/**
 * The same answer as a promise, for a caller outside the wizard (the clinic's
 * registration forms ask it through hooks/useClinicRegion.js). Unlike the hook it
 * REJECTS when the doctor BFF cannot answer, so a caller can tell «not foreign»
 * from «nobody knows».
 */
export function fetchAccountRegion() {
  const iranMarket = isIranMarket();
  const asked = Promise.resolve()
    .then(() => bffQuery(ONBOARDING_CONTEXT))
    .then((data) => {
      const ctx = data?.onboardingContext;
      if (iranMarket && !ctx?.foreign) return HOME;
      const country = countryForPhone(ctx?.phone)?.iso2 || null;
      return { foreign: true, country: iranMarket || country !== 'IR' ? country : null };
    });
  return iranMarket ? asked : asked.catch(() => ABROAD);
}

/** `{ foreign, country }` — country is an iso2 code, only ever set when foreign. */
export function useAccountRegion() {
  const [region, setRegion] = React.useState(unknownRegion);

  React.useEffect(() => {
    let live = true;
    fetchAccountRegion()
      .then((answer) => { if (live && answer.foreign) setRegion(answer); })
      .catch(() => { /* not foreign — the wizard as it always was */ });
    return () => { live = false; };
  }, []);

  return region;
}

/**
 * The same answer, but `null` until it is known — for a screen that shows
 * something different to each side and must not flash the wrong one: «شروع»
 * offers an Iranian account the کد نظام پزشکی and a foreign one the wizard.
 * A failure resolves to "not foreign", exactly like the hook above.
 */
export function useResolvedAccountRegion() {
  const [region, setRegion] = React.useState(null);

  React.useEffect(() => {
    let live = true;
    fetchAccountRegion()
      .then((answer) => { if (live) setRegion(answer); })
      .catch(() => { if (live) setRegion(unknownRegion()); });
    return () => { live = false; };
  }, []);

  return region;
}

// Euro members and the microstates that use the euro as their own.
const EURO = new Set([
  'AT', 'BE', 'HR', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 'LV', 'LT',
  'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES', 'AD', 'MC', 'SM', 'VA', 'ME', 'XK',
]);

const OWN_CURRENCY = {
  AE: 'AED', SA: 'SAR', QA: 'QAR', TR: 'TRY', KW: 'KWD', BH: 'BHD', OM: 'OMR', IQ: 'IQD',
  US: 'USD', GB: 'GBP', CH: 'CHF', LI: 'CHF',
};

/**
 * The currency a clinic in `country` most likely keeps its books in, from the
 * closed list the platform supports. A country whose own currency is not on it
 * (Egypt's pound, today) starts on USD — a starting point the founder can change,
 * never IRR, which is the one answer that is certainly wrong abroad.
 */
export function currencyForCountry(country) {
  const code = EURO.has(country) ? 'EUR' : OWN_CURRENCY[country];
  return code && CURRENCY_CODES.includes(code) ? code : 'USD';
}

/**
 * The one country a clinic's currency points at, or null when it points at
 * several — the inverse of `currencyForCountry`, read off the same table so the
 * two cannot disagree. IRR is Iran (every clinic before the choice existed, and
 * never offered abroad) and IQD is Iraq; USD is not the United States, because
 * it is where every country the platform has no currency for starts, the euro is
 * twenty countries and the franc two.
 */
export function countryForCurrency(code) {
  if (code === DEFAULT_CURRENCY) return 'IR';
  if (code === 'USD') return null;
  const named = Object.keys(OWN_CURRENCY).filter((country) => OWN_CURRENCY[country] === code);
  return named.length === 1 ? named[0] : null;
}

/** The clinic currencies offered to a founder from abroad: all but IRR. */
export const isOfferedAbroad = (code) => code !== DEFAULT_CURRENCY;

/**
 * Where the map opens for a founder from abroad who has not placed a pin yet:
 * the middle of their country, at a zoom that shows it whole. Only a starting
 * view — the pin is theirs to place — so a country missing here opens on a
 * world view rather than on somebody else's capital.
 */
const COUNTRY_VIEW = {
  IQ: [33.2, 43.7, 6], SA: [24.0, 45.0, 5], AE: [24.2, 54.4, 7], KW: [29.3, 47.7, 8],
  QA: [25.3, 51.2, 9], BH: [26.0, 50.55, 10], OM: [21.5, 57.0, 6], JO: [31.2, 36.5, 7],
  LB: [33.9, 35.9, 8], SY: [35.0, 38.5, 7], PS: [31.9, 35.2, 8], YE: [15.6, 48.0, 6],
  EG: [26.8, 30.8, 6], LY: [27.0, 17.0, 5], TN: [34.0, 9.5, 6], DZ: [28.0, 2.6, 5],
  MA: [31.8, -7.1, 6], SD: [15.5, 30.2, 5], MR: [20.3, -10.9, 5], SO: [5.2, 46.2, 5],
  DJ: [11.8, 42.6, 8], KM: [-11.9, 43.9, 8], TR: [39.0, 35.2, 6], AF: [33.9, 67.7, 6],
};

const WORLD_VIEW = [20, 0, 2];

/** `[lat, lng, zoom]` to open the map on, for a founder from abroad. */
export const mapViewForCountry = (country) => COUNTRY_VIEW[country] || WORLD_VIEW;
